import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { logout } from '../api/client';
import { useAuthStore } from '../auth/store';
import { Bell, ChevronDown, Mail, Menu, Moon, Sun } from 'lucide-react';
import { NavMenu } from './NavMenu';
import { SectionTabs } from './SectionTabs';
import { toast } from './Toasts';
import { Icon } from './ui';
import { useThemeStore } from '../theme/store';

// The authenticated operational shell: a fixed sidebar (brand + permission
// gated NavMenu), a topbar (notifications bell + user menu / sign out), and
// the routed content area via <Outlet/>. Domain pages render inside <main>.
export function AppShell(): JSX.Element {
  const me = useAuthStore((s) => s.me);
  const setMe = useAuthStore((s) => s.setMe);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Forced password change takes precedence over every other gate: admin-
  // created accounts and admin password resets set must_change_password, and
  // the user is locked to the change-password screen until they clear it.
  const mustChangePw = me?.user.must_change_password === true;
  useEffect(() => {
    if (mustChangePw && pathname !== '/force-password-change') {
      navigate('/force-password-change', { replace: true });
    }
  }, [mustChangePw, pathname, navigate]);

  // Dormant 2FA mandate: /me only sets requires_2fa_enrollment once the
  // require_2fa system setting is flipped on the server. Until then this
  // effect never fires. When active, un-enrolled users are routed to
  // Settings until they complete enrollment.
  const mustEnroll2fa = me?.requires_2fa_enrollment === true;
  useEffect(() => {
    if (!mustChangePw && mustEnroll2fa && pathname !== '/settings') {
      toast.warn('Two-factor authentication is required — set it up to continue.');
      navigate('/settings', { replace: true });
    }
  }, [mustChangePw, mustEnroll2fa, pathname, navigate]);

  const [menuOpen, setMenuOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false); // mobile drawer
  const userRef = useRef<HTMLDivElement>(null);

  const themeMode = useThemeStore((s) => s.mode);
  const toggleTheme = useThemeStore((s) => s.toggle);

  // Close the user menu on outside click / Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (userRef.current && !userRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const onLogout = async () => {
    await logout().catch(() => {});
    setMe(null);
    navigate('/login', { replace: true });
  };

  const displayName = me?.user.display_name ?? me?.user.email ?? 'Operator';
  const initials = displayName
    .split(/\s+/)
    .slice(0, 2)
    .map((p: string) => p[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <div className={`app-shell ${navOpen ? 'nav-open' : ''}`}>
      <aside className="app-sidebar">
        <div className="app-brand">
          <img src="/logo-mark.png" alt="" className="app-brand-logo" />
          <span className="app-brand-name">
            Darkspire <span className="brand-risk">Risk</span>
          </span>
        </div>
        <NavMenu />
        <div className="app-sidebar-footer">
          <span className="app-env-tag">Operations Console</span>
        </div>
      </aside>

      {/* Scrim closes the mobile drawer when tapped. */}
      <div
        className="app-scrim"
        role="presentation"
        onClick={() => setNavOpen(false)}
      />

      <div className="app-body">
        <header className="app-topbar">
          <button
            type="button"
            className="app-nav-toggle"
            aria-label="Toggle navigation"
            aria-expanded={navOpen}
            onClick={() => setNavOpen((v) => !v)}
          >
            <Icon icon={Menu} size={20} />
          </button>
          <div className="app-topbar-spacer" />
          <button
            type="button"
            className="app-bell"
            title={themeMode === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            aria-label="Toggle light or dark theme"
            onClick={toggleTheme}
          >
            <Icon icon={themeMode === 'dark' ? Sun : Moon} size={18} />
          </button>
          <button
            type="button"
            className="app-bell"
            title="Notifications"
            aria-label="Notifications"
          >
            <Icon icon={Bell} size={18} />
          </button>
          <Link
            to="/me/webmail"
            className="app-webmail"
            title="Webmail"
            aria-label="Webmail"
          >
            <Icon icon={Mail} size={18} />
          </Link>
          <div className="app-user" ref={userRef}>
            <button
              type="button"
              className="app-user-trigger"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              <span className="app-user-avatar" aria-hidden="true">
                {initials || 'U'}
              </span>
              <span className="app-user-name">{displayName}</span>
              <span className="app-user-caret" aria-hidden="true">
                <Icon icon={ChevronDown} size={14} />
              </span>
            </button>
            {menuOpen && (
              <div className="app-user-menu" role="menu">
                <div className="app-user-menu-head">
                  <div className="app-user-menu-name">{displayName}</div>
                  {me?.user.email && (
                    <div className="app-user-menu-email">{me.user.email}</div>
                  )}
                </div>
                <button
                  type="button"
                  role="menuitem"
                  className="app-user-menu-item"
                  onClick={() => {
                    setMenuOpen(false);
                    navigate('/settings');
                  }}
                >
                  Settings
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="app-user-menu-item"
                  onClick={onLogout}
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </header>

        <SectionTabs />

        <main className="app-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
